import type { UseFormReturn } from 'react-hook-form';
import { useContext } from 'react';
import {
  generateThemeFromSourceColor,
  isValidHexColor,
  Labeled,
  TextInputField,
  ThemeSetterContext,
} from '@sixui/ui';
import { useTranslation } from 'react-i18next';

import type { IContainerProps } from '~/components/utils/Container';
import type { IAppFormValues } from './AppForm';
import { FormField } from '~/components/atoms/FormField';
import { FormSection } from '~/components/atoms/FormSection';
import { CustomColorInputField } from '~/components/organisms/fields/CustomColorInputField';
import { FormFileUploader } from '~/components/organisms/fields/FileUploader';
import { OrganizationSelector } from '~/components/organisms/fields/OrganizationSelector';
import { IFormMode } from '~/helpers/types';

export type IAppFormMainSectionProps = IContainerProps & {
  form: UseFormReturn<IAppFormValues>;
  mode: IFormMode;
  appId?: string;
};

export const AppFormMainSection: React.FC<IAppFormMainSectionProps> = (
  props,
) => {
  const { sx, form, mode, appId } = props;
  const { t } = useTranslation();
  const themeSetterContext = useContext(ThemeSetterContext);

  return (
    <FormSection sx={sx}>
      <Labeled
        label={t('AppFormMainSection.fields.name.label')}
        supportingText={t('AppFormMainSection.fields.name.supportingText')}
        required
      >
        <FormField
          name="name"
          control={form.control}
          as={TextInputField}
          forwardError
        />
      </Labeled>
      <Labeled
        label={t('AppFormMainSection.fields.icon.label')}
        supportingText={t('AppFormMainSection.fields.icon.supportingText')}
      >
        <FormFileUploader
          name="icons"
          control={form.control}
          appId={appId}
          maxFiles={1}
          acceptTypes={['picture']}
        />
      </Labeled>

      {mode === 'creation' ? (
        <>
          <Labeled
            label={t('AppFormMainSection.fields.themeColorPrimary.label')}
            supportingText={t(
              'AppFormMainSection.fields.themeColorPrimary.supportingText',
            )}
          >
            <FormField
              name="themeColorPrimary"
              control={form.control}
              as={CustomColorInputField}
              forwardError
              rules={{
                onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
                  const color = event.target.value;
                  if (isValidHexColor(color)) {
                    themeSetterContext?.setTheme(
                      generateThemeFromSourceColor(color),
                    );
                  }
                },
              }}
              palette="regular"
              quantizeColorCount={8}
            />
          </Labeled>
          <Labeled label={t('common.account')}>
            <FormField
              name="organizationId"
              control={form.control}
              as={OrganizationSelector}
              forwardError
              allowPersonalAccount
            />
          </Labeled>
        </>
      ) : null}
    </FormSection>
  );
};
